import { RouteRecordRaw } from 'vue-router';
import { useBrandConfig } from 'src/composables/useBrandConfig';

const { isGapGPT } = useBrandConfig();

const getLoginPage = () => {
  return () => import('pages/LoginPage.vue');
};

const routes: RouteRecordRaw[] = [
   {
      path: '/onboarding',
      children: [
         {
            path: '',
            name: 'onboarding',
            component: () => import('pages/v2/onboarding/OnboardingPage.vue'),
            meta: {
               requiresAuth: true,
               redirectTo: '/login?redirect=/onboarding'
            }
         },
      ],
   },
   {
      path: '/chat',
      component: () => import('layouts/v2/MainLayout.vue'),
      children: [
         {
            path: 'models',
            name: 'models',
            component: () => import('pages/ModelsPage.vue'),
            meta: {
               requiresAuth: true,
               redirectTo: '/login?redirect=/chat/models'
            }
         },
         {
            path: ':token',
            name: 'existing-chat',
            component: () => import('pages/v2/chat/ChatPage.vue'),
            meta: {showDisclaimer: true}
         },
         {
            path: '',
            name: 'chat',
            component: () => import('pages/v2/chat/LandingChatPage.vue'),
            meta: {showDisclaimer: true}
         },
      ],
   },
   {
      path: '/project',
      component: () => import('layouts/v2/MainLayout.vue'),
      children: [
         {
            path: ':token',
            name: 'project-chat',
            component: () => import('pages/v2/project/ProjectChatPage.vue'),
            meta: {showDisclaimer: true}
         },
      ],
   },
   {
      path: '/app/irimc',
      name: 'irimc-entry',
      component: () => import('pages/app/IrimcEntryPage.vue'),
   },
   {
      path: '/chat-search',
      component: undefined,
      children: [
         {
            path: ':token',
            name: 'chat-search',
            component: () => import('pages/ExtensionNewTabPage.vue'),
            meta: { showDisclaimer: false }
         },
         {
            path: '',
            name: 'chat-search-new',
            component: () => import('pages/ExtensionNewTabPage.vue'),
            meta: { showDisclaimer: false }
         },
      ],
   },
   {
      path: '/voice',
      component: () => import('layouts/v2/MainLayout.vue'),
      children: [
         {
            path: ':token',
            name: 'voice',
            component: () => import('pages/v2/chat/VoicePage.vue'),
         },
      ],
   },
   ...(isGapGPT ? [{
      path: '/all-models',
      redirect: '/models',
   }] : []),
   {
      path: '/app/models',
      redirect: '/chat/models',
   },
   ...(isGapGPT ? [{
      path: '/models',
      children: [
         {
            path: '',
            name: 'all-models',
            component: () => import('pages/seo/AllModelsPage.vue'),
         },
         {
            path: 'gemini-pro',
            redirect: '/gemini',
         },
         {
            path: ':name',
            name: 'model-seo',
            component: () => import('pages/seo/BaseModelPage.vue'),
         },
      ],
   }] : []),
   {
      path: '/gpts',
      component: () => import('layouts/v2/MainLayout.vue'),
      children: [
         {
            path: '',
            name: 'gpts',
            component: () => import('pages/GPTsPage.vue'),
         }
      ]
   },
   {
      path: '/image-gen',
      component: () => import('layouts/v2/MainLayout.vue'),
      children: [
         {
            path: '',
            name: 'images',
            component: () => import('pages/ImagesPage.vue'),
         }
      ]
   },
   {
      path: '/gpt-builder/',
      children: [
         {
            path: ':token',
            name: 'existing-gpt-builder',
            component: () => import('pages/core/GPTBuilder.vue'),
         },
         {
            path: 'new',
            name: 'new-gpt-builder',
            component: () => import('pages/core/GPTBuilder.vue'),
         },
      ],
   },
   {
      path: '/share/:token',
      children: [
         {
            path: '',
            name: 'sharedChat',
            component: () => import('pages/SharedChat.vue'),
         },
      ],
   },
   {
      path: '/subscription',
      component: () => import('layouts/v2/MainLayout.vue'),
      children: [
         {
            path: '',
            name: 'subscription',
            component: () => import('pages/settings/SubscriptionPage.vue'),
            meta: {
               requiresAuth: true,
               redirectTo: '/login?redirect=/subscription'
            }
         },
      ],
   },
   {
      path: '/subscriptions',
      redirect: '/subscription',
   },
   ...(isGapGPT ? [{
      path: '/partnership_panel',
      name: 'partnership_panel',
      component: () => import('pages/AffiliatePanelPage.vue'),
      meta: {
         requiresAuth: true,
         redirectTo: '/login?redirect=/partnership_panel'
      }
   },
   {
      path: '/partnership_panel/deposits',
      name: 'partnership_panel_deposits',
      component: () => import('pages/AffiliateDepositsPage.vue'),
      meta: {
         requiresAuth: true,
         redirectTo: '/login?redirect=/partnership_panel/deposits'
      }
   }] : []),
   ...(isGapGPT ? [{
      path: '/ads/:advertiser_id/create',
      name: 'ads-create',
      component: () => import('pages/ads/AdFormPage.vue'),
      meta: {
        requiresAuth: true,
        redirectTo: '/login?redirect=/ads'
      }
   }] : []),
   {
      path: '/',
      children: [
         {
            path: 'rules',
            name: 'rules',
            component: () => import('pages/info/Rules.vue'),
         },
         {
            path: 'about-us',
            name: 'aboutus',
            component: () => import('pages/info/AboutUs.vue'),
         },
         {
            path: 'bugbounty',
            name: 'bug-bounty',
            component: () => import('pages/BugBountyPage.vue'),
         },
         {
            path: 'bug-bounty',
            redirect: '/bugbounty',
         },
   ],
  },
  {
     path: '/enterprise',
     name: 'enterprise',
     component: () => import('pages/EnterprisePage.vue'),
  },
  {
     path: '/contact-sales',
     name: 'contact-sales',
     component: () => import('pages/ContactSales.vue'),
  },
  ...(isGapGPT ? [{
     path: '/status',
     name: 'status',
     component: () => import('pages/api_v2/StatusPage.vue'),
  }] : []),
  {
     path: '/',
     children: [
         {
            path: 'payment',
            name: 'payment',
            component: () => import('pages/Payment.vue'),
         },
         {
            path: 'payment-bridge',
            name: 'payment-bridge',
            component: () => import('pages/PaymentBridgePage.vue'),
         },
        {
           path: 'direct-debit/bank-selection',
           name: 'direct-debit-bank-selection',
           component: () => import('pages/DirectDebitBankSelection.vue'),
           meta: {
              requiresAuth: true,
              redirectTo: '/login?redirect=/direct-debit/bank-selection'
           }
        },
        {
           path: 'direct-debit/result',
           name: 'direct-debit-result',
           component: () => import('pages/DirectDebitResult.vue'),
        },
         {
            path: 'login',
            name: 'login',
            component: getLoginPage(),
         },
         {
            path: 'login-extension',
            name: 'login-extension',
            component: () => import('pages/LoginPageExtension.vue')
         },
         {
            path: 'loginab',
            name: 'loginab',
            component: getLoginPage(),
         },
         {
            path: 'sharif',
            name: 'sharif',
            component: () => import('pages/app/SharifLand.vue'),
         },
         {
            path: 'b/sale/login',
            name: 'fake-login',
            component: getLoginPage(),
         },
         {
            path: '',
            name: 'home',
            component: getLoginPage(),
         },
         {
            path: 'pricing',
            name: 'pricing',
            component: () => import('pages/PricingPage.vue'),
         },
         {
            path: 'pricing-v2',
            name: 'pricing-v2',
            component: () => import('pages/PricingV2Page.vue'),
         },
         ...(isGapGPT ? [{
            path: 'download',
            name: 'download',
            component: () => import('pages/DownloadPage.vue'),
         }] : []),
         {
            path: 'app/bazaar',
            name: 'app-bazaar',
            component: () => import('pages/app/BazaarStartPage.vue'),
         },
         {
            path: 'app/myket',
            name: 'app-myket',
            component: () => import('pages/app/MyketStartPage.vue'),
         },
         {
            path: 'app/google_play',
            name: 'app-google-play',
            component: () => import('pages/app/GooglePlayStartPage.vue'),
         },
         {
            path: 'app/huawei',
            name: 'app-huawei',
            component: () => import('pages/app/HuaweiStartPage.vue'),
         },
         {
            path: 'app/samsung',
            name: 'app-samsung',
            component: () => import('pages/app/SamsungStartPage.vue'),
         },
         {
            path: 'app/xiaomi',
            name: 'app-xiaomi',
            component: () => import('pages/app/XiaomiStartPage.vue'),
         },
         {
            path: 'app',
            name: 'app',
            component: () => import('pages/app/AppPage.vue'),
         },
         {
            path: 'workspace',
            name: 'workspace-members',
            component: () => import('pages/workspace/WorkspaceMembers.vue'),
            meta: {
               requiresAuth: true,
               redirectTo: '/login?redirect=/workspace'
            }
         },
         {
            path: 'workspace/billing',
            name: 'workspace-billing',
            component: () => import('pages/workspace/WorkspaceBilling.vue'),
            meta: {
               requiresAuth: true,
               redirectTo: '/login?redirect=/workspace/billing'
            }
         },
         {
            path: 'workspace/analytics',
            name: 'workspace-analytics',
            component: () => import('pages/workspace/WorkspaceAnalytics.vue'),
            meta: {
               requiresAuth: true,
               redirectTo: '/login?redirect=/workspace/analytics'
            }
         },
         ...(isGapGPT ? [{
            path: 'jobs',
            name: 'jobs',
            component: () => import('pages/JobsPage.vue'),
         }] : []),
         {
            path: 'privacy',
            name: 'play-store-privacy',
            component: () => import('pages/info/PrivacyPolicy.vue'),
         },
         ...(isGapGPT ? [{
            path: 'gemini',
            name: 'gemini',
            component: () => import('pages/GeminiPage.vue'),
         }] : []),
         {
            path: 'delete-info',
            name: 'play-store-delete',
            component: () => import('pages/info/DeleteInfo.vue'),
         },
         {
            path: 'tickets',
            name: 'tickets',
            component: () => import('pages/TicketsPage.vue'),
            meta: {
              requiresAuth: true,
              redirectTo: '/login?redirect=/tickets'
            }
         },
         {
            path: 'tickets/new',
            name: 'new-ticket',
            component: () => import('pages/NewTicketPage.vue'),
            meta: {
              requiresAuth: true,
              redirectTo: '/login?redirect=/tickets/new'
            }
         },
         {
            path: 'tickets/:token',
            name: 'ticket-chat',
            component: () => import('pages/support/SupportChatRoom.vue'),
            meta: {
              requiresAuth: true,
              redirectTo: '/login?redirect=/tickets'
            }
         },
         ...(isGapGPT ? [{
            path: 'ads/rules',
            name: 'ads-rules',
            component: () => import('pages/ads/AdRulesPage.vue'),
         },
         {
            path: 'ads/script',
            name: 'ads-script-shortlink',
            component: () => import('pages/ads/AdsScriptPage.vue'),
            meta: {
              requiresAuth: true,
              redirectTo: '/login?redirect=/ads/script'
            }
         },
         {
            path: 'ads/:advertiser_id?',
            name: 'ads',
            component: () => import('pages/ads/AdsPage.vue'),
         },
          {
             path: 'ads/:advertiser_id/deposits',
             name: 'ads-deposits',
             component: () => import('pages/ads/DepositsPage.vue'),
             meta: {
               requiresAuth: true,
               redirectTo: '/login?redirect=/ads'
             }
          },
         {
            path: 'ads/:advertiser_id/script',
            name: 'ads-script',
            component: () => import('pages/ads/AdsScriptPage.vue'),
            meta: {
              requiresAuth: true,
              redirectTo: '/login?redirect=/ads'
            }
         },
         {
            path: 'ads/:advertiser_id/profile',
            name: 'ads-profile',
            component: () => import('pages/ads/ProfilePage.vue'),
         },
         {
            path: 'ads/profile/new',
            name: 'ads-profile-new',
            component: () => import('pages/ads/ProfilePage.vue'),
         },
         {
            path: 'ads/:advertiser_id/edit/:id',
            name: 'ads-edit',
            component: () => import('pages/ads/AdFormPage.vue'),
            meta: {
              requiresAuth: true,
              redirectTo: '/login?redirect=/ads'
            }
         }] : []),
         {
            path: 'support-chats',
            redirect: to => ({ path: '/tickets', query: to.query, hash: to.hash }),
         },
         {
            path: 'support-chats/:token',
            redirect: to => ({
               name: 'ticket-chat',
               params: { token: to.params.token },
               query: to.query,
               hash: to.hash
            }),
         },
      ],
   },
   ...(isGapGPT ? [{
      path: '/platform',
      children: [
         {
            path: '',
            redirect: '/platform-v2',
         },
         {
            path: 'dashboard',
            redirect: '/platform-v2',
         },
         {
            path: 'tokens',
            redirect: '/platform-v2/tokens',
         },
         {
            path: 'wallet',
            redirect: '/platform-v2/wallet',
         },
         {
            path: 'usage',
            redirect: '/platform-v2/usage',
         },
         {
            path: 'logs',
            redirect: '/platform-v2/logs',
         },
         {
            path: 'quickstart',
            redirect: '/platform-v2/docs/quickstart',
         },
         {
            path: 'pricing',
            redirect: '/platform-v2/pricing',
         },
         {
            path: 'models',
            redirect: '/platform-v2/models',
         },
         {
            path: 'docs/use-cases/cursor',
            redirect: '/platform-v2/docs/use-cases/cursor',
         },
         {
            path: 'docs/use-cases/cline',
            redirect: '/platform-v2/docs/use-cases/cline',
         },
         {
            path: 'docs/use-cases/n8n',
            redirect: '/platform-v2/docs/use-cases/n8n',
         },
         {
            path: 'docs/use-cases/claude-code',
            redirect: '/platform-v2/docs/use-cases/claude-code',
         },
         {
            path: 'docs/use-cases/codex-cli',
            redirect: '/platform-v2/docs/use-cases/codex-cli',
         },
         {
            path: 'docs/models/gemini',
            redirect: '/platform-v2/docs/models/gemini',
         },
         {
            path: 'docs/models/deepseek-v3-iran',
            redirect: '/platform-v2/docs/models/qwen-3-5-iran',
         },
         {
            path: 'docs/models/qwen-3-5-iran',
            redirect: '/platform-v2/docs/models/qwen-3-5-iran',
         },
         {
            path: 'status',
            redirect: '/status',
         },
      ],
   }] : []),
   // Platform V2 - New design
   ...(isGapGPT ? [{
      path: '/platform-v2',
      component: () => import('layouts/api/ApiLayoutV2.vue'),
      children: [
         {
            path: '',
            name: 'api-v2-dashboard',
            component: () => import('pages/api_v2/DashboardPage.vue'),
            meta: {
              requiresAuth: true,
              redirectTo: '/login?api=true'
            }
         },
         {
            path: 'tokens',
            name: 'api-v2-tokens',
            component: () => import('pages/api_v2/TokensPage.vue'),
            meta: {
              requiresAuth: true,
              redirectTo: '/login?api=true'
            }
         },
         {
            path: 'wallet',
            name: 'api-v2-wallet',
            component: () => import('pages/api_v2/WalletPage.vue'),
            meta: {
              requiresAuth: true,
              redirectTo: '/login?api=true'
            }
         },
         {
            path: 'usage',
            name: 'api-v2-usage',
            component: () => import('pages/api_v2/UsagePage.vue'),
            meta: {
              requiresAuth: true,
              redirectTo: '/login?api=true'
            }
         },
         {
            path: 'logs',
            name: 'api-v2-logs',
            component: () => import('pages/api_v2/LogsPage.vue'),
            meta: {
              requiresAuth: true,
              redirectTo: '/login?api=true'
            }
         },
         {
            path: 'docs',
            redirect: '/platform-v2/docs/quickstart',
         },
         {
            path: 'docs/quickstart',
            name: 'api-v2-quickstart',
            component: () => import('pages/api_v2/docs/QuickStartPage.vue'),
         },
         {
            path: 'quickstart',
            redirect: '/platform-v2/docs/quickstart',
         },
         {
            path: 'pricing',
            name: 'api-v2-pricing',
            component: () => import('pages/api_v2/docs/ModelsPage.vue'),
         },
         {
            path: 'models',
            name: 'api-v2-models',
            component: () => import('pages/api_v2/docs/ModelsPage.vue'),
         },
         {
            path: 'docs/use-cases/cursor',
            name: 'api-v2-docs-use-cases-cursor',
            component: () => import('pages/api_v2/docs/use-cases/CursorUseCasePage.vue'),
         },
         {
            path: 'docs/use-cases/cline',
            name: 'api-v2-docs-use-cases-cline',
            component: () => import('pages/api_v2/docs/use-cases/ClineUseCasePage.vue'),
         },
         {
            path: 'docs/use-cases/n8n',
            name: 'api-v2-docs-use-cases-n8n',
            component: () => import('pages/api_v2/docs/use-cases/N8nUseCasePage.vue'),
         },
         {
            path: 'docs/use-cases/claude-code',
            name: 'api-v2-docs-use-cases-claude-code',
            component: () => import('pages/api_v2/docs/use-cases/ClaudeCodeUseCasePage.vue'),
         },
         {
            path: 'docs/use-cases/codex-cli',
            name: 'api-v2-docs-use-cases-codex-cli',
            component: () => import('pages/api_v2/docs/use-cases/CodexCliUseCasePage.vue'),
         },
         {
            path: 'docs/models/gemini',
            name: 'api-v2-docs-models-gemini',
            component: () => import('pages/api_v2/docs/models/GeminiPage.vue'),
         },
         {
            path: 'docs/models/deepseek-v3-iran',
            redirect: '/platform-v2/docs/models/qwen-3-5-iran',
         },
         {
            path: 'docs/models/qwen-3-5-iran',
            name: 'api-v2-docs-models-qwen-3-5-iran',
            component: () => import('pages/api_v2/docs/models/Qwen35IranPage.vue'),
         },
         {
            path: 'status',
            redirect: '/status',
         },
      ],
   }] : []),
   ...(isGapGPT ? [{
      path: '/ai-api',
      component: () => import('layouts/api/ApiLayout.vue'),
      children: [
         {
            path: '',
            component: () => import('pages/api/LandingPage.vue'),
         }
      ]
   },
   {
      path: '/gemini-api',
      component: () => import('layouts/api/ApiLayout.vue'),
      children: [
         {
            path: '',
            name: 'gemini-api',
            component: () => import('pages/api/docs/models/GeminiPage.vue'),
         }
      ]
   },
   {
      path: '/ai-pricing',
      component: () => import('layouts/api/ApiLayout.vue'),
      children: [
         {
            path: '',
            name: 'ai-pricing',
            component: () => import('pages/api/docs/ModelsPage.vue'),
         }
      ]
   }] : []),
   {
      path: '/mcp/oauth/callback',
      name: 'mcp-oauth-callback',
      component: () => import('pages/mcp/OAuthCallbackPage.vue'),
   },
   ...(isGapGPT ? [{
      path: '/oauth/cli',
      name: 'oauth-cli',
      component: () => import('pages/oauth/OAuthCliPage.vue'),
   }] : []),
   ...(isGapGPT ? [{
      path: '/gapcode',
      name: 'gapcode-install',
      component: () => import('pages/codex/GapCodeInstallPage.vue'),
   },
   {
      path: '/gapcode/install',
      redirect: '/gapcode',
   },
   {
      path: '/gapcode/wallet',
      name: 'gapcode-wallet',
      component: () => import('pages/codex/CodexWalletPage.vue'),
      meta: { requiresAuth: true, redirectTo: '/login?redirect=/gapcode/wallet' },
   },
   {
      path: '/gapgpt-cli/wallet',
      redirect: '/gapcode/wallet',
   }] : []),
   ...(isGapGPT ? [{
      path: '/translate',
      name: 'translate',
      component: () => import('pages/TranslatePage.vue'),
   }] : []),
   ...(isGapGPT ? [{
      path: '/co-work',
      name: 'co-work',
      component: () => import('pages/CoWorkPage.vue'),
   }] : []),
   // Always leave this as last one,
   // but you can also remove it
   {
      path: '/:catchAll(.*)*',
      component: () => import('pages/info/ErrorNotFound.vue'),
   },
];

export default routes;
